import { Observable, of } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';

import { FormConfig } from '../admin/config/base-form.config';
import { FormField } from '../admin/form-controls/form-field';
import { defaultListSlotLabel, listSlotItemName } from './list-slot-defaults';
import { tableShortfallWarning } from './event-table-warning';

export interface EventFormGameConfig {
    /** Extra fields inside each list slot — e.g. Warlord's Order Dice Limit. */
    listSlotFields?: FormField[];
    /** Extra fields at the foot of the Lists section — e.g. K47's rules group. */
    listSettingsFields?: FormField[];
    /** Merged over the shared defaults. */
    defaultValue?: Record<string, any>;
}

/**
 * Sections, not groups. A section's fields are flattened into root-level form
 * controls by createForm(); a group nests them under the group's own name, which
 * the API's field allowlist would then drop. Collapsing is presentation only.
 */
/**
 * The creation step: the two fields that stay required on a draft (see §6 of
 * the event publish lifecycle spec), and nothing else.
 *
 * Everything else an event needs is optional until publication, so asking for
 * it up front only gives the organiser a wall of inputs before they have
 * anything to look at. They get an event to work on in two fields, and the
 * full editor immediately afterwards.
 *
 * Deliberately without the `onChange` the full form's startDate carries: that
 * handler keeps endDate at or after startDate, and there is no endDate control
 * here. It guards for a missing control, so copying it across would be
 * harmless -- but a no-op handler only invites the next reader to wonder what
 * it was for.
 */
export function buildEventCreateForm(): Observable<FormConfig> {
    return of({
        formDefinition: [
            {
                type: 'textbox',
                name: 'title',
                label: 'Event Title',
                required: true
            },
            {
                type: 'datepicker',
                name: 'startDate',
                label: 'Start Date',
                required: true
            }
        ]
    } as FormConfig);
}

export function buildEventForm(game: EventFormGameConfig = {}): Observable<FormConfig> {
    return of({
        formDefinition: [
            {
                type: 'textbox',
                name: 'title',
                label: 'Event Title',
                required: true
            },
            {
                type: 'markdown',
                name: 'description',
                label: 'Description',
                description: 'A short summary of the event — shown prominently on the event page.'
            },
            {
                type: 'section',
                header: 'Where and when',
                fields: [
                    {
                        type: 'venue-search',
                        name: 'venueSearch',
                        label: 'Venue',
                        description: 'Search for the venue by name to fill in its address, timezone and currency.'
                    },
                    {
                        type: 'section',
                        cssClass: 'horizontal',
                        fields: [
                            {
                                type: 'datepicker',
                                name: 'startDate',
                                required: true,
                                label: 'Start Date',
                                onChange: (value: string, formGroup: UntypedFormGroup) => {
                                    const root = formGroup.root as UntypedFormGroup;
                                    const endDateControl = root.get('endDate');
                                    if (!endDateControl) {
                                        return;
                                    }
                                    const endDate = endDateControl.value;
                                    if (!endDate || endDate < value) {
                                        endDateControl.setValue(value);
                                    }
                                }
                            },
                            {
                                type: 'datepicker',
                                name: 'endDate',
                                label: 'End Date'
                            }
                        ]
                    }
                ]
            },
            {
                type: 'section',
                header: 'Address',
                collapsed: true,
                description: 'Filled in automatically when you pick a venue above. Open this to enter an address by hand.',
                fields: [
                    {
                        type: 'section',
                        cssClass: 'horizontal',
                        fields: [
                            {
                                type: 'country-select',
                                name: 'location.countryCode',
                                label: 'Country',
                                required: true
                            },
                            {
                                type: 'textbox',
                                name: 'location.venueName',
                                label: 'Venue Name'
                            }
                        ]
                    },
                    {
                        type: 'section',
                        cssClass: 'horizontal',
                        fields: [
                            {
                                type: 'textbox',
                                name: 'location.city',
                                label: 'City'
                            },
                            {
                                type: 'textbox',
                                name: 'location.state',
                                label: 'State / Region'
                            }
                        ]
                    },
                    {
                        type: 'textbox',
                        name: 'location.postalCode',
                        label: 'Postal Code'
                    },
                    {
                        type: 'hidden',
                        name: 'location.coordinates'
                    },
                    {
                        type: 'textbox',
                        name: 'timeZone',
                        label: 'Time Zone',
                        description: 'IANA time zone (e.g. America/New_York, Europe/London)'
                    }
                ]
            },
            {
                type: 'section',
                header: 'Registration',
                collapsed: true,
                description: 'How many players, who can see the event, and how sign-ups are handled.',
                fields: [
                    {
                        type: 'number',
                        name: 'capacity',
                        required: true,
                        label: 'Capacity'
                    },
                    {
                        type: 'select',
                        name: 'visibility',
                        label: 'Visibility',
                        options: [
                            { label: 'Public', value: 'public' },
                            { label: 'Private', value: 'private' }
                        ]
                    },
                    {
                        type: 'select',
                        name: 'registrationApprovalMode',
                        label: 'Registration Approval',
                        options: [
                            { label: 'Auto-accept', value: 'auto' },
                            { label: 'TO Approval Required', value: 'manual' }
                        ]
                    },
                    {
                        type: 'checkbox',
                        name: 'waitlistEnabled',
                        label: 'Enable Waitlist?'
                    },
                    {
                        type: 'checkbox',
                        name: 'autoPromoteWaitlist',
                        label: 'Auto-promote Waitlist (first come, first served)?'
                    },
                    {
                        type: 'textbox',
                        name: 'contactEmail',
                        label: 'Contact Email',
                        description:
                            'Shown to players so they have a way to reach you — e.g. after being removed from the event. Defaults to your own account email but can be changed to a different address, such as a private alias.'
                    }
                ]
            },
            {
                type: 'section',
                header: 'Tournament',
                collapsed: true,
                description: 'Rounds, pairings and how results are scored. Leave these alone for a casual game day.',
                fields: [
                    {
                        type: 'section',
                        cssClass: 'horizontal',
                        fields: [
                            {
                                type: 'number',
                                name: 'numberOfRounds',
                                label: 'Number of Rounds'
                            },
                            {
                                type: 'number',
                                name: 'numberOfTables',
                                label: 'Number of Tables',
                                description:
                                    'How many tables the venue has. Spare tables are used to keep players off tables they have already played at. Leave empty for one table per game.'
                            },
                            {
                                // Renders nothing at all when the count is fine; .table-warning:empty
                                // collapses the wrapper so there is no gap in the row.
                                type: 'display',
                                cssClass: 'table-warning',
                                hideLabel: true,
                                html: true,
                                render: (formValue: any) => tableShortfallWarning(formValue) ?? ''
                            },
                            {
                                type: 'select',
                                name: 'pairingMode',
                                label: 'Pairing Mode',
                                options: [
                                    { label: 'Swiss', value: 'swiss' },
                                    { label: 'Round Robin', value: 'round_robin' },
                                    { label: 'Random', value: 'random' }
                                ]
                            }
                        ]
                    },
                    {
                        type: 'markdown',
                        name: 'resultScoringRules',
                        label: 'Result Scoring Rules',
                        description:
                            'Shown to players when they report a game result — explain how to calculate victory points for this event.'
                    },
                    {
                        type: 'array',
                        name: 'resultFields',
                        label: 'Result Fields',
                        itemName: 'Field',
                        nameField: 'label',
                        description:
                            'What decides the standings, in the order the standings rank on — the top field is primary, and each field below it only breaks ties left by the fields above. Match Points and Victory Points are included by default, matching the classic 3/1/0 scheme. Drag to reorder.',
                        newItemDefaults: () => ({
                            type: 'number',
                            outcomeMode: 'none',
                            outcomeValues: { win: 0, draw: 0, loss: 0 },
                            byeValue: 0
                        }),
                        fields: [
                            {
                                type: 'hidden',
                                name: 'id'
                            },
                            {
                                type: 'hidden',
                                name: 'type'
                            },
                            {
                                type: 'textbox',
                                name: 'label',
                                label: 'Field Label',
                                required: true
                            },
                            {
                                type: 'select',
                                name: 'outcomeMode',
                                label: 'Scoring Mode',
                                description:
                                    "Reported: both players enter their own number for this field, which is ranked on but decides nothing about who won — e.g. an in-game score. Set from Win/Draw/Loss: the server fills this field in from the points below once the game's outcome is known — this is how a fixed scheme like 3/1/0 match points is built. Decides the Winner: both players report this field's number, same as Reported, but whoever reports the higher number is treated as having won the game (equal is a draw). At most one field may decide the winner.",
                                options: [
                                    { label: 'Reported by both players', value: 'none' },
                                    { label: 'Set from Win/Draw/Loss', value: 'from_outcome' },
                                    { label: 'Decides the Winner', value: 'derives_outcome' }
                                ]
                            },
                            {
                                type: 'group',
                                name: 'outcomeValues',
                                header: 'Points for Win / Draw / Loss',
                                // Shown only in 'Set from Win/Draw/Loss' mode -- parentFormGroup here
                                // is this result field's own form group (the array item), the same
                                // pattern bolt-action-3e's option-limit fields already use to show
                                // fields conditional on a sibling select (unit-option.shared.ts).
                                if: (_formValue: any, parentFormGroup: any) =>
                                    parentFormGroup?.controls?.outcomeMode?.value === 'from_outcome',
                                fields: [
                                    { type: 'number', name: 'win', label: 'Win' },
                                    { type: 'number', name: 'draw', label: 'Draw' },
                                    { type: 'number', name: 'loss', label: 'Loss' }
                                ]
                            },
                            {
                                type: 'number',
                                name: 'byeValue',
                                label: 'Value Awarded on a Bye',
                                description:
                                    'Ignored in "Set from Win/Draw/Loss" mode -- a bye there always awards this field\'s own Win value instead, since a bye is a win.'
                            }
                        ]
                    },
                    {
                        type: 'checkbox',
                        name: 'revealOpponentList',
                        label: "Reveal opponents' submitted lists before each game?"
                    },
                    {
                        type: 'checkbox',
                        name: 'requireCheckIn',
                        label: 'Require players to check in before Round 1 starts?'
                    }
                ]
            },
            {
                type: 'section',
                header: 'Lists',
                collapsed: true,
                description: 'What force lists players must submit, and by when.',
                fields: [
                    {
                        type: 'array',
                        name: 'listSlots',
                        label: 'List Slots',
                        itemName: 'Slot',
                        nameField: 'label',
                        // Without this, an unnamed slot's collapsed header falls back to the
                        // generic "(New Slot)" (array-field-item-name.pipe.ts) — indistinguishable
                        // from every other unnamed slot. mapItemName is the form system's existing
                        // per-field hook for overriding that fallback.
                        mapItemName: listSlotItemName,
                        description:
                            'Define how many force lists participants must submit. In most cases this will be 1 list, but some events use an escalation format where later games use a higher points value — in that case, add a slot per round with its own points limit.',
                        // A single slot's label was never actually required, but a freshly-added
                        // slot with no label displays as the generic "(New Slot)" —
                        // indistinguishable from any other unlabelled slot — which reads as
                        // broken rather than optional. Seeding a real, sensible label fixes
                        // both: it no longer looks unfinished, and nobody has to invent a name
                        // for what is, in most events, the only slot there'll ever be. Once
                        // there's more than one, the backend requires every slot to have a
                        // real label (event.schema.ts), so this also keeps a freshly-added
                        // second slot from failing to save until the organiser notices.
                        newItemDefaults: (items: any[]) => ({
                            label: defaultListSlotLabel(items.length),
                            required: true
                        }),
                        fields: [
                            {
                                type: 'hidden',
                                name: 'id'
                            },
                            {
                                type: 'textbox',
                                name: 'label',
                                label: 'Slot Label'
                            },
                            {
                                type: 'number',
                                name: 'pointsLimit',
                                label: 'Points Limit'
                            },
                            ...(game.listSlotFields ?? []),
                            {
                                type: 'checkbox',
                                name: 'required',
                                label: 'Required?'
                            }
                        ]
                    },
                    {
                        type: 'datepicker',
                        name: 'listSubmissionDate',
                        label: 'List Submission Deadline',
                        description:
                            'Players can submit lists after this date, but only the TO can withdraw a submitted list once the deadline has passed.'
                    },
                    {
                        type: 'checkbox',
                        name: 'requireInAppLists',
                        label: 'Require lists to be submitted via the app?',
                        description: 'Reduces the manual validation work you need to do, but blocks non-subscribers from registering.'
                    },
                    {
                        type: 'checkbox',
                        name: 'autoAcceptCleanLists',
                        label: 'Automatically accept lists with no validation issues?',
                        description:
                            'Applies to in-app lists whose validation reports no errors and no warnings. You can still reject one afterwards. Text lists always need accepting by hand, as there is nothing to validate.'
                    },
                    ...(game.listSettingsFields ?? [])
                ]
            },
            {
                type: 'section',
                header: 'Schedule',
                collapsed: true,
                description: 'The running order for the day, shown to players on the event-day page.',
                fields: [
                    {
                        type: 'section',
                        header: 'Build a schedule',
                        description: 'Describe the day and generate the blocks below. You can adjust any of them afterwards.',
                        fields: [
                            {
                                type: 'section',
                                cssClass: 'horizontal',
                                fields: [
                                    { type: 'number', name: 'generatorGames', label: 'Games per day' },
                                    { type: 'number', name: 'generatorGameMinutes', label: 'Game length (minutes)' }
                                ]
                            },
                            {
                                type: 'section',
                                cssClass: 'horizontal',
                                fields: [
                                    { type: 'number', name: 'generatorBreakMinutes', label: 'Break between games (minutes)' },
                                    { type: 'number', name: 'generatorRegistrationMinutes', label: 'Registration (minutes)' }
                                ]
                            },
                            // No "day starts at" here: the generator always opens the day at 09:00 on the
                            // event's own start date, and the timeline below has a single "Day starts at"
                            // control that moves the whole schedule. A datepicker here self-populated with
                            // the current date and time, so it generated a day on today's date for an event
                            // months away, and it duplicated the timeline's own control with a different
                            // value for the same concept.
                            { type: 'number', name: 'generatorResultsMinutes', label: 'Results & prizes (minutes)' }
                        ]
                    },
                    {
                        type: 'schedule-timeline',
                        name: 'scheduleBlocks',
                        label: 'Schedule',
                        description: "The day's timeline — rounds, lunch breaks, day-end. Shown to players on the event-day page."
                    }
                ]
            },
            {
                type: 'section',
                header: 'Additional Information',
                collapsed: true,
                description:
                    'Anything players need to know that the sections above do not cover \u2014 parking, food, accessibility, house rules. Each section is headed by you.',
                fields: [
                    {
                        type: 'array',
                        name: 'infoSections',
                        label: 'Sections',
                        hideLabel: true,
                        itemName: 'Section',
                        nameField: 'heading',
                        description:
                            'Shown on the event page and as their own page in the generated event pack. Line breaks are kept as you type them.',
                        fields: [
                            { type: 'textbox', name: 'heading', label: 'Heading' },
                            { type: 'markdown', name: 'body', label: 'Details' }
                        ]
                    }
                ]
            },
            {
                type: 'section',
                header: 'Ticketing',
                collapsed: true,
                description: 'Ticket price and how players pay you. Payment is handled off-platform for now.',
                fields: [
                    {
                        type: 'section',
                        cssClass: 'horizontal',
                        fields: [
                            {
                                type: 'number',
                                name: 'ticketPrice',
                                label: 'Ticket Price'
                            },
                            {
                                type: 'textbox',
                                name: 'currency',
                                label: 'Currency (e.g. USD, GBP)',
                                description: 'ISO 4217 currency code'
                            }
                        ]
                    },
                    {
                        type: 'markdown',
                        name: 'paymentInstructions',
                        label: 'Payment Instructions'
                    }
                ]
            }
        ],
        defaultValue: {
            pairingMode: 'swiss',
            visibility: 'public',
            registrationApprovalMode: 'auto',
            waitlistEnabled: false,
            autoPromoteWaitlist: false,
            numberOfRounds: 4,
            // Match Points primary, Victory Points breaking ties -- today's hardcoded
            // 3/1/0 scheme, now just two ordinary fields. Victory Points is deliberately
            // 'none', not 'derives_outcome': players still pick the winner themselves by
            // default, same as before this feature existed. An organiser wanting VP to
            // decide the winner sets its mode to "Decides the Winner" and reorders it
            // above Match Points.
            resultFields: [
                {
                    id: 0,
                    label: 'Match Points',
                    type: 'number',
                    outcomeMode: 'from_outcome',
                    outcomeValues: { win: 3, draw: 1, loss: 0 },
                    byeValue: 0
                },
                { id: 1, label: 'Victory Points', type: 'number', outcomeMode: 'none', byeValue: 0 }
            ],
            generatorGameMinutes: 120,
            generatorBreakMinutes: 15,
            generatorRegistrationMinutes: 30,
            generatorResultsMinutes: 15,
            ...game.defaultValue
        }
    } as FormConfig);
}
